import { toUserDateKey } from '@/lib/user-date-time';

import type { ColumnFilterCategory, ColumnHeaderFilter } from './types';

/**
 * `passesHeaderFilter` — predykat header filtra wyekstrahowany z
 * `ConfigurableTable` `processedData` (bliźniak `sort-utils.ts` / `compareCellValues`).
 * Trzymany osobno, bo porównania liczb/dat jako string są podatne na CICHY błąd —
 * wydzielenie pozwala testować tę logikę wprost, bez montowania całego komponentu.
 */

/**
 * Dzień ISO (`YYYY-MM-DD`) z wartości komórki — czysta data bez zmian, data-czas ze strefą
 * (moment w czasie) → dzień w strefie przeglądarki (`toUserDateKey`), żeby porównanie
 * z granicą-dniem (DatePicker emituje `YYYY-MM-DD`) było inkluzywne i zgodne z tym, co widać na karcie.
 * Zwraca `null`, gdy wartość nie ma kształtu ISO; wtedy wołający spada na starą ścieżkę
 * leksykalną (zero zmiany zachowania dla nie-dat).
 */
function isoDay(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  return toUserDateKey(s) ?? s.slice(0, 10);
}

/**
 * Wartość komórki jako string do porównań leksykalnych — dla obiektów `JSON.stringify`
 * (parytet ze starą ścieżką `columnFilters`), inaczej `"[object Object]"` psułoby
 * porównanie na tabelach lokalnych z akcesorami zwracającymi obiekty.
 */
function toComparable(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Lokalne aplikowanie header filtra do wartości komórki. `category` (typ kolumny
 * z `headerFilter.category`) decyduje o SPOSOBIE porównania w trybach `range`/
 * `condition`:
 *  - `number` → porównanie NUMERYCZNE (parseFloat) zamiast leksykalnego,
 *  - `date`   → porównanie po DNIU ISO (obcięcie daty-czasu do dnia),
 *  - inne / brak → zachowanie legacy (leksykalne / numeryczne-gdy-parsuje).
 *
 * ZASADA BEZ REGRESJI: typowane porównanie włącza się WYŁĄCZNIE gdy wartości
 * faktycznie są liczbą / datą ISO; w każdym innym przypadku funkcja spada na
 * dokładnie tę samą ścieżkę co przed poprawką. Tryb `values` niezmieniony.
 * Powód poprawki: bez `category` zakres liczb/dat porównywał się leksykalnie
 * (`"9" > "100"`, timestamp gubił ostatni dzień, warunek datowy zwijał rok
 * przez `parseFloat("2026-08-10") === 2026`).
 */
export function passesHeaderFilter(
  cellValue: unknown,
  filter: ColumnHeaderFilter,
  category?: ColumnFilterCategory,
): boolean {
  // Tryb "wartości" — lista checkboxów (niezmieniony)
  if (filter.mode === 'values') {
    const selected = filter.selectedValues ?? [];
    if (selected.length === 0) return true;
    if (cellValue == null) return selected.includes(null);
    return selected.includes(String(cellValue));
  }

  // Tryb "zakres" — od/do
  if (filter.mode === 'range') {
    const { rangeMin, rangeMax } = filter;
    // Tryb "pojedyncza data" — RÓWNOŚĆ dnia (checkbox „Pojedyncza data" w filtrze daty).
    // Porównanie po dniu ISO (spójne z gałęzią `date` niżej): komórka z timestampem
    // trafia do swojego dnia niezależnie od godziny/strefy; brak granicy → przepuść.
    if (filter.singleDate) {
      const target = (rangeMin ?? rangeMax ?? '').slice(0, 10);
      if (!target) return true;
      const day = isoDay(cellValue);
      return day !== null ? day === target : String(cellValue ?? '').slice(0, 10) === target;
    }
    if (category === 'number') {
      const n = parseFloat(String(cellValue));
      const min = rangeMin ? parseFloat(rangeMin) : null;
      const max = rangeMax ? parseFloat(rangeMax) : null;
      if (
        !Number.isNaN(n) &&
        (min === null || !Number.isNaN(min)) &&
        (max === null || !Number.isNaN(max))
      ) {
        if (min !== null && n < min) return false;
        if (max !== null && n > max) return false;
        return true;
      }
      // wartość/granica nienumeryczna → legacy leksykalny (bez regresji)
    } else if (category === 'date') {
      const day = isoDay(cellValue);
      if (day !== null) {
        if (rangeMin && day < rangeMin) return false;
        if (rangeMax && day > rangeMax) return false;
        return true;
      }
      // wartość nie-ISO → legacy leksykalny
    }
    const cellStr = toComparable(cellValue);
    if (rangeMin && cellStr < rangeMin) return false;
    if (rangeMax && cellStr > rangeMax) return false;
    return true;
  }

  // Tryb "warunek" — operator + wartość
  if (filter.mode === 'condition') {
    const op = filter.operator;
    if (!op) return true;
    if (op === 'is_null') return cellValue == null || cellValue === '';
    if (op === 'is_not_null') return cellValue != null && cellValue !== '';

    const condValue = filter.conditionValue ?? '';
    const cellStr = toComparable(cellValue).toLowerCase();
    const condStr = condValue.toLowerCase();

    // Operatory czysto tekstowe — te same dla każdego typu kolumny.
    switch (op) {
      case 'contains':
        return cellStr.includes(condStr);
      case 'not_contains':
        return !cellStr.includes(condStr);
      case 'starts_with':
        return cellStr.startsWith(condStr);
      case 'ends_with':
        return cellStr.endsWith(condStr);
    }

    // Operatory porządkowe / równości — porównanie po typie kolumny.
    if (category === 'date') {
      const cellDay = isoDay(cellValue);
      const condDay = isoDay(condValue);
      if (cellDay !== null && condDay !== null) {
        switch (op) {
          case 'eq':
            return cellDay === condDay;
          case 'neq':
            return cellDay !== condDay;
          case 'gt':
            return cellDay > condDay;
          case 'gte':
            return cellDay >= condDay;
          case 'lt':
            return cellDay < condDay;
          case 'lte':
            return cellDay <= condDay;
          default:
            return true;
        }
      }
      // wartość/warunek nie-ISO → legacy niżej
    }

    const cellNum = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue ?? ''));
    const condNum = parseFloat(condValue);
    const numeric = !Number.isNaN(cellNum) && !Number.isNaN(condNum);
    switch (op) {
      case 'eq':
        return category === 'number' && numeric ? cellNum === condNum : cellStr === condStr;
      case 'neq':
        return category === 'number' && numeric ? cellNum !== condNum : cellStr !== condStr;
      case 'gt':
        return numeric ? cellNum > condNum : cellStr > condStr;
      case 'gte':
        return numeric ? cellNum >= condNum : cellStr >= condStr;
      case 'lt':
        return numeric ? cellNum < condNum : cellStr < condStr;
      case 'lte':
        return numeric ? cellNum <= condNum : cellStr <= condStr;
      default:
        return true;
    }
  }

  return true;
}
